import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _get from 'lodash/get';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Element, Events, animateScroll as scroll, scrollSpy, scroller } from 'react-scroll';
import classes from './PostDetail.module.css';
import ParagraphsText from 'components/common/ParagraphsText';
import PostMapThumb from 'components/Maps/Post/PostMapThumbnail/PostMapThumbnail';
import VideoModal from 'components/Modals/VideoModal/VideoModal';
import WhoCanSee from 'components/Posts/PostDetail/WhoCanSee/WhoCanSee';
import PostLogShareSelector from 'components/Posts/PostLogShareSelector/PostLogShareSelector';
import Dialogue from 'components/UI/Dialogue/Dialogue';
import OptionsDropdown from 'components/UI/OptionsDropdown/OptionsDropdown';
import Spinner from 'components/UI/Spinner/Spinner';
import PostDetailCommentForm from 'containers/Comments/PostDetailCommentForm/PostDetailCommentForm';
import FullscreenMap from 'containers/Maps/FullscreenMap/FullscreenMap';
import PostDetailComments from 'containers/Posts/PostDetailComments/PostDetailComments';
import HelpTile from 'containers/UI/HelpTile/HelpTile';
import ImagesCarousel from 'containers/UI/ImagesCarousel/ImagesCarousel';
import { getMediaThumb } from 'shared/util';
import {
  categoryImages,
  formatPostDetailDateRange,
  formatTimeRangeForPostsTableDisplay,
  getRgbFromColorName,
  sortArrayOfObjectsByDateAsc,
} from 'shared/utility';
import * as actions from 'store/actions';
import { getUserProfileDetails } from 'store/thunks';

const PostDetail = (props) => {
  const params = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const history = useHistory();

  const [state, setState] = useState({
    images: [],
    currentLogId: null,
    showContributeForm: false,
    showDuplicatePostDialogue: false,
    showDeleteCommentDialogue: false,
    showGiftPostSentDialogue: false,
    showImagesCarousel: false,
    showFullscreenMap: false,
    selectedImageIndex: null,
    renderedCommentImage: null,
    showWhoCanSee: false,
    showLogSharer: false,
    showPostDetailNavOptions: false,
    showLogSwitchDropdown: false,
    selectedLogs: [],
    postHasBeenEdited: false,
    deletingCommentId: null,
    showVideo: false,
    videoURL: '',
  });
  const toggleVideo = (url) => {
    setState((prevState) => ({ ...prevState, showVideo: !state.showVideo, videoURL: url }));
  };
  const commentsSectionContainer = useRef(null);

  const post = useSelector((s) => s.post.fetchedPreviewPost || s.post.fetchedPost || {});
  const {
    loading: postLoading,
    deleting,
    showPostDetailNavOptions,
    // submitted,
    // isDraft,
    commentAdded,
    postHasBeenEdited,
    submittedPostIsGift,
  } = useSelector((s) => s.post || {});
  const postingAs = useSelector((s) => s.user.postingAs);
  const isManagedUser = useSelector(
    (s) => s.user.signedInAs.UserId && s.user.postingAs.UserId && s.user.postingAs.UserId !== s.user.signedInAs.UserId,
  );
  const onFetchPost = useCallback(
    (PostId, LogId, GroupId, IsDraft, UserId, IsManagedUser) => dispatch(actions.fetchPost(PostId, LogId, GroupId, IsDraft, UserId, IsManagedUser)),
    [dispatch],
  );
  const onInitPostForm = useCallback(
    (postId, IsDraft, isGift, redirectPath, UserId, IsManagedUser) =>
      dispatch(actions.initPostFormStart(postId, IsDraft, isGift, redirectPath, UserId, IsManagedUser)),
    [dispatch],
  );
  const onTogglePostDetailNavOptions = useCallback(() => dispatch(actions.togglePostDetailNavOptions()), [dispatch]);
  const onDuplicatePost = useCallback((postId, IsDraft, copyNumber) => dispatch(actions.duplicatePost(postId, IsDraft, copyNumber)), [dispatch]);
  const onUpdatePost = (
    PostId,
    LogIds,
    Name,
    Type,
    DateStart,
    DateEnd,
    TimeStart,
    TimeEnd,
    Desc,
    AddFileIds,
    Suggested,
    WhatTags,
    WhoTags,
    LocationTags,
    RemovedImageIds,
    Ongoing,
    Location,
    Location2,
    LocIcon,
    LocFav,
    FavId,
    MapType,
    GeoLocation,
    Center,
    Pitch,
    Heading,
    Zoom,
    IsDraft,
    CopyCommentFileIds,
    IsManagedUser,
    UserId,
  ) =>
    dispatch(
      actions.editPost(
        PostId,
        LogIds,
        Name,
        Type,
        DateStart,
        DateEnd,
        TimeStart,
        TimeEnd,
        Desc,
        AddFileIds,
        Suggested,
        WhatTags,
        WhoTags,
        LocationTags,
        RemovedImageIds,
        Ongoing,
        Location,
        Location2,
        LocIcon,
        LocFav,
        FavId,
        MapType,
        GeoLocation,
        Center,
        Pitch,
        Heading,
        Zoom,
        IsDraft,
        CopyCommentFileIds,
        IsManagedUser,
        UserId,
      ),
    );
  const onAddCommentImageToPost = useCallback(
    (PostId, CommentImageId) => dispatch(actions.addCommentImageToPost(PostId, CommentImageId)),
    [dispatch],
  );
  const onUpdateComment = useCallback(
    (CommentId, Comment, Delete, IsManagedUser, UserId, WhoTags, WhatTags, LocationTags) =>
      dispatch(actions.entryCommentUpdate(CommentId, Comment, Delete, IsManagedUser, UserId, WhoTags, WhatTags, LocationTags)),
    [dispatch],
  );

  const handlePostFetch = useCallback(() => {
    const userId = isManagedUser ? postingAs.UserId : null;
    if (params.postId) onFetchPost(params.postId, null, null, false, userId, isManagedUser);
  }, [isManagedUser, onFetchPost, postingAs.UserId, params.postId]);

  const commentFormScroll = (toTop) => {
    const targetElement = toTop ? 'comment-form-top' : 'comment-form-bottom';
    scroller.scrollTo(targetElement, {
      smooth: true,
      delay: 250,
      duration: 1500,
      containerId: 'containerElement',
      offset: 0, // Scrolls to element + 50 pixels down the page
    });
  };

  const toggleContributeForm = () => {
    const commentsSectionContainerHeight = commentsSectionContainer.current.offsetHeight;
    if (commentsSectionContainerHeight === window.innerHeight - 165) {
      if (state.showContributeForm === false) {
        commentFormScroll(false);
      } else {
        scroll.scrollToTop({
          containerId: 'containerElement',
          smooth: true,
          duration: 1500,
        });
      }
    }
    setState((prevState) => ({ ...prevState, showContributeForm: !state.showContributeForm }));
  };

  const closeContributeForm = () => {
    setState((prevState) => ({ ...prevState, showContributeForm: false }));
  };

  const handleKeyDown = useCallback(
    (e) => {
      if (e.keyCode === 27) {
        if (showPostDetailNavOptions === true) {
          onTogglePostDetailNavOptions();
        } else if (state.showImagesCarousel) {
          setState((prevState) => ({ ...prevState, showImagesCarousel: false }));
        } else if (state.showLogSharer) {
          setState((prevState) => ({ ...prevState, showLogSharer: false }));
        }
      }
    },
    [onTogglePostDetailNavOptions, showPostDetailNavOptions, state.showImagesCarousel, state.showLogSharer],
  );

  const handleLogSelect = (logId) => {
    let selectedLogs = [...state.selectedLogs];
    if (state.selectedLogs.includes(logId)) {
      selectedLogs = selectedLogs.filter((logID) => logID !== logId);
    } else {
      selectedLogs.push(logId);
    }
    setState((prevState) => ({ ...prevState, selectedLogs }));
  };

  const toggleDuplicatePostDialogue = () => {
    setState((prevState) => ({ ...prevState, showDuplicatePostDialogue: !state.showDuplicatePostDialogue, showPostDetailNavOptions: false }));
  };

  const togglePostLogsSharer = () => {
    if (!state.showLogSharer) {
      onTogglePostDetailNavOptions();
    }
    setState((prevState) => ({ ...prevState, showLogSharer: !state.showLogSharer }));
  };

  const handlePostLogsUpdate = () => {
    onUpdatePost(
      post.Post.PostId,
      state.selectedLogs,
      post.Post.Name,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      isManagedUser,
      postingAs.UserId,
    );
    togglePostLogsSharer();
  };

  const editPost = () => {
    onInitPostForm(post.Post.PostId, false, false, null, postingAs, isManagedUser);
    setState((prevState) => ({ ...prevState, showPostDetailNavOptions: false }));
  };

  const giftDraft = () => {
    onInitPostForm(post.Post.PostId, false, true, location.pathname, postingAs, isManagedUser);
    setState((prevState) => ({ ...prevState, showPostDetailNavOptions: false }));
  };

  const duplicatePost = () => {
    onDuplicatePost(post.Post.PostId, false, null);
    toggleDuplicatePostDialogue();
  };

  const addTagToPost = (tag, tagType) => {
    const whatTags = [post.Tags.WhatTags];
    const whoTags = [post.Tags.WhoTags];
    const locationTags = [post.Tags.LocationTags];
    if (tagType === 'WhatTags') {
      whatTags.push(tag);
    } else if (tagType === 'WhoTags') {
      whoTags.push(tag);
    } else {
      locationTags.push(tag);
    }

    onUpdatePost(
      post.Post.PostId,
      post.Logs.Logs.map((log) => log.Id),
      post.Post.Name,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      whatTags,
      whoTags,
      locationTags,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      null,
      isManagedUser,
      postingAs.UserId,
    );
  };

  const handleAddPostImage = (addedImageId) => {
    onAddCommentImageToPost(post.Post.PostId, addedImageId);
  };

  const handleDeleteComment = () => {
    onUpdateComment(state.deletingCommentId, null, true, isManagedUser, postingAs.UserId, null);
    setState((prevState) => ({ ...prevState, deletingCommentId: null, showDeleteCommentDialogue: false }));
  };

  const toggleImagesCarousel = (index) => {
    if (state.renderedCommentImage) {
      setState((prevState) => ({
        ...prevState,
        showImagesCarousel: !prevState.showImagesCarousel,
        selectedImageIndex: 0,
        renderedCommentImage: null,
      }));
    } else {
      setState((prevState) => ({ ...prevState, showImagesCarousel: !prevState.showImagesCarousel, selectedImageIndex: index }));
    }
  };

  const renderCommentImage = (selectedCommentImage) => {
    setState((prevState) => ({
      ...prevState,
      renderedCommentImage: selectedCommentImage,
      showImagesCarousel: true,
      selectedImageIndex: 0,
    }));
    toggleImagesCarousel(0);
  };

  const toggleDeleteCommentDialogue = (commentId) => {
    if (commentId) {
      setState((prevState) => ({ ...prevState, showDeleteCommentDialogue: true, deletingCommentId: commentId }));
    } else {
      setState((prevState) => ({ ...prevState, showDeleteCommentDialogue: false, deletingCommentId: null }));
    }
  };

  const toggleElement = (stateRenderName) => {
    setState((prevState) => ({ ...prevState, [stateRenderName]: !prevState[stateRenderName] }));
  };

  const returnToLog = useCallback(() => {
    const postId = location.pathname.replace('/life-shared/posts/', '');
    const path =
      location.state !== undefined && location.state.prevPath && !location.state.prevPath.includes('preview')
        ? location.state.prevPath
        : '/life-shared/timeline/';
    history.push({
      pathname: path,
      state: { postId, prevPath: 'posts', edited: state.postHasBeenEdited },
    });
    setState((prevState) => ({ ...prevState, showLogSwitchDropdown: false }));
  }, [location.pathname, location.state, history, state.postHasBeenEdited]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.scrollTop = 0; // For Safari
    document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    if (!props.isPreview) {
      Events.scrollEvent.register('begin', () => {});
      Events.scrollEvent.register('end', () => {});
    }
    return () => {
      if (!props.isPreview) {
        Events.scrollEvent.remove('begin');
        Events.scrollEvent.remove('end');
      }
    };
  }, [props.isPreview]);

  useEffect(() => {
    if (!props.isPreview) {
      const currentLogId = params.logId;
      setState((prevState) => ({ ...prevState, currentLogId }));

      scrollSpy.update();
      if (postingAs.Logs) {
        setState((prevState) => ({ ...prevState, selectedLogs: postingAs.Logs.map((log) => log.Id) }));
      }
    }
  }, [params.logId, postingAs.Logs, postingAs.UserId, props.isPreview]);

  useEffect(() => {
    handlePostFetch();
  }, [handlePostFetch]);

  useEffect(() => {
    if (commentAdded) {
      handlePostFetch();
    }
  }, [handlePostFetch, commentAdded]);

  useEffect(() => {
    // If post was gifted successfully, show the GiftPostSentDialogue
    if (submittedPostIsGift) {
      // setState((prevState) => ({ ...prevState, showGiftPostSentDialogue: true }));
    }
  }, [submittedPostIsGift]);

  useEffect(() => {
    // If post has been edited, store that in state
    // We'll use this to ensure that when returning to log, log is refreshed if post has been edited
    setState((prevState) => ({ ...prevState, postHasBeenEdited: true }));
  }, [postHasBeenEdited]);

  useEffect(() => {
    if (commentAdded) {
      setState((prevState) => ({ ...prevState, showContributeForm: false }));
    }
  }, [commentAdded]);

  useEffect(() => {
    if (post.Logs && postingAs.Logs) {
      setState((prevState) => ({ ...prevState, selectedLogs: post.Logs.map((log) => log.Id) }));
    }
  }, [post.Logs, postingAs.Logs]);

  useEffect(() => {
    if (deleting) returnToLog();
  }, [deleting, returnToLog]);

  useEffect(() => {
    return () => {
      if (showPostDetailNavOptions) {
        onTogglePostDetailNavOptions();
      }
    };
  }, [onTogglePostDetailNavOptions, showPostDetailNavOptions]);

  if (postLoading) {
    return (
      <div className={classes.PostDetailOuterContainer}>
        <Spinner lightColor />
      </div>
    );
  }

  let postTitle = null;
  let categoryImage = null;
  let displayDate = null;
  let author = null;
  let createdBy = null;
  let authorColor = null;
  let postDesc = null;
  const tagsArray = [];

  const postUserDetails = post.Post && postingAs ? getUserProfileDetails(postingAs, post.Post.UserId) : {};

  const logIdsForPost = post && post.Logs && post.Logs.length > 0 ? post.Logs.map((log) => log.Id) : [];
  const whoCanSeeList = post ? post.Users : [];
  const PostAddCommenContainerClasses = [classes.PostAddCommenContainer];
  if (state.showContributeForm) PostAddCommenContainerClasses.push(classes.Shown);
  const postDetailContainerClasses = [classes.PostDetailContainer];
  const mediaFiles = post && (props.isPreview ? props.postImages : post.Files && post.Files.Files);
  const carouselImages = state.renderedCommentImage ? [state.renderedCommentImage] : mediaFiles;

  if (post.Post) {
    postTitle = post.Post.Name;
    categoryImage = categoryImages.find((category) => category.name === post.Post.Type);
    displayDate = `${formatPostDetailDateRange(post.Post.DateStartCalc, post.Post.DateEndCalc)}\n${formatTimeRangeForPostsTableDisplay(
      post.Post.TimeStartCalc,
      post.Post.TimeEndCalc,
      post.Post.Ongoing,
    )}`;
    author = [_get(postUserDetails, 'FirstName', ''), _get(postUserDetails, 'LastName')].join(' ');
    authorColor = postUserDetails && postUserDetails.Color ? getRgbFromColorName(postUserDetails.Color) : 'rgb(108, 162, 183)';
    const knownUser = (postingAs.KnownUsers || []).find((user) => user.User.UserId === post.Post.UserId);
    if (post.Post.UserId !== post.Post.CreatedByUserId && knownUser) {
      createdBy = knownUser.User.ManagedBy;
    }
    postDesc = post.Post.Desc;
    // returnLogUrl = props.isPreview
    //   ? null
    //   : `/life-shared/timeline/${location.state && location.state.logId ? location.state.logId : ''}`;
    post.Tags.WhatTags.forEach((tag) => tagsArray.push({ tagType: 'WhatTag', value: tag }));
    post.Tags.WhoTags.forEach((tag) => tagsArray.push({ tagType: 'WhoTag', value: tag }));
    post.Tags.LocationTags.forEach((tag) => tagsArray.push({ tagType: 'LocationTag', value: tag }));
  }

  if (props.isPreview) postDetailContainerClasses.push(classes.Preview);

  if (post.Post && (post.Post.Location || post.Post.Location2) && state.showFullscreenMap) {
    const center = { lat: parseFloat(post.Post.MapLat), lng: parseFloat(post.Post.MapLng) };
    const geoLocation = post.Post.Lat ? { lat: parseFloat(post.Post.Lat), lng: parseFloat(post.Post.Lng) } : center;
    return (
      <div className={classes.PostDetailOuterContainer}>
        <FullscreenMap
          close={() => toggleElement('showFullscreenMap')}
          show={state.showFullscreenMap}
          geoLocation={geoLocation}
          center={center}
          zoom={post.Post.Zoom}
          location={post.Post.Location ? post.Post.Location : ''}
          location2={post.Post.Location2}
          locationIcon={post.Post.LocIcon}
          markerColor={postUserDetails && postUserDetails.Color && getRgbFromColorName(postUserDetails.Color)}
          mapTypeId={post.Post.MapType ? post.Post.MapType : 'roadmap'}
          pitch={post.Pitch}
          heading={post.Post.Heading}
          locIcon={post.Post.LocIcon}
        />
      </div>
    );
  }
  return (
    <div className={classes.PostDetailOuterContainer}>
      {state.showVideo && <VideoModal show={state.showVideo} toggleVideo={toggleVideo} videoUrl={state.videoURL} orientation="horizontal" />}
      {state.showDuplicatePostDialogue && (
        <Dialogue
          subject="Create Duplicate"
          message="A copy of this post - with Contributions removed - will be added to your Drafts."
          cancelText="Cancel"
          confirmationText="OK"
          confirm={duplicatePost}
          cancelled={toggleDuplicatePostDialogue}
          className="DuplicatePost"
        />
      )}
      {state.showGiftPostSentDialogue && (
        <Dialogue
          subject="Gift Post Sent"
          message="The member(s) you sent this to will get a draft of this post to use as they wish. They can find it in their Drafts folder."
          confirmationText="OK"
          confirm={() => setState((prevState) => ({ ...prevState, showGiftPostSentDialogue: false }))}
          className="DuplicatePost"
        />
      )}
      {state.showDeleteCommentDialogue && (
        <Dialogue
          subject="Delete contribution"
          message="Are you sure you want to delete this contribution?"
          cancelText="Cancel"
          confirmationText="Delete"
          confirm={handleDeleteComment}
          cancelled={toggleDeleteCommentDialogue}
          className="DuplicatePost"
          dialogueType="DialogueDanger"
        />
      )}
      {post.Post && (
        <PostLogShareSelector
          close={() => setState((prevState) => ({ ...prevState, showLogSharer: false }))}
          show={state.showLogSharer}
          logs={postingAs.Logs || []}
          handleLogSelect={handleLogSelect}
          selectedLogs={state.selectedLogs}
          handlePostLogsUpdate={handlePostLogsUpdate}
        />
      )}
      {!state.showImagesCarousel && (
        <div className={postDetailContainerClasses.join(' ')}>
          <div className={classes.Header}>
            <div className={classes.LogHeader}>
              {!props.isPreview && <FontAwesomeIcon icon="chevron-left" className={classes.ReturnArrow} onClick={returnToLog} />}
              <h2 />
            </div>
            {post.Post && postingAs.UserId === post.Post.UserId && !props.isPreview && (
              <div id="post-detail-options" className={classes.PostDetailNavOptions} onClick={() => toggleElement('showPostDetailNavOptions')}>
                <h1>...</h1>
              </div>
            )}
          </div>
          {state.showPostDetailNavOptions && (
            <OptionsDropdown
              className="PostDetailEllipses"
              headerText="What would you like to do with this post?"
              options={[
                { text: 'Edit', action: editPost, name: 'edit-post' },
                { text: 'Gift this Post', action: giftDraft, name: 'gift-draft' },
                { text: 'Create a Duplicate', action: toggleDuplicatePostDialogue, name: 'duplicate-post' },
                { text: 'Share to Different Log(s)', action: togglePostLogsSharer, name: 'change-log-sharing' },
                { text: 'Create New Post', action: () => onInitPostForm(null), name: 'create-new-post' },
              ]}
            />
          )}
          <div className={classes.ContentContainer}>
            <div className={classes.LeftColumn}>
              <h1 id="post-title" className={classes.PostTitle}>
                {postTitle}
              </h1>
              <div className={classes.CategoryDateAuthor}>
                <div className={classes.CategoryImage}>{categoryImage && <categoryImage.image />}</div>
                <div className={classes.DateAndAuthor}>
                  <p className={classes.Date}>{displayDate}</p>
                  <p className={classes.Author} style={{ color: authorColor }}>
                    {author + (createdBy ? `, added by ${createdBy.FirstName} ${createdBy.LastName}` : '')}
                  </p>
                </div>
              </div>
              <ParagraphsText text={postDesc} className={classes.Description} />
              <div className={classes.Tags}>
                {tagsArray.map((tag) => (
                  <span key={`${tag.tagType}-${tag.value}`} className={[classes.Tag, classes[tag.tagType]].join(' ')}>
                    {tag.value}
                  </span>
                ))}
              </div>
              {post && post.Logs && post.Logs.length > 0 && (
                <div className={classes.LogsList}>
                  <h4>Logs</h4>
                  {post.Logs.map((log) => (
                    <p className={classes.LogListItem} key={log.Id}>
                      {log.Name}
                    </p>
                  ))}
                </div>
              )}
              {post.Post && (post.Post.Location || post.Post.Location2) && (
                <PostMapThumb
                  locationIcon={post.Post.LocIcon}
                  markerColor={postUserDetails && postUserDetails.Color && getRgbFromColorName(postUserDetails.Color)}
                  post={post ? post.Post : post}
                  clicked={() => toggleElement('showFullscreenMap')}
                />
              )}
            </div>
            {mediaFiles && (
              <div className={classes.MiddleColumn}>
                {mediaFiles.map((media, i) => {
                  return media.MediaType === 'Video' && !props.isPreview ? (
                    <HelpTile
                      key={media.Id}
                      toggle={() => toggleVideo(media.FileURL)}
                      videoUrl={media.MediumImageURL}
                      imageUrl={getMediaThumb(media)}
                      videoLengthInSeconds={media.MediaType === 'Video' ? media.DurationInMs / 1000 : null}
                    />
                  ) : (
                    <img
                      onClick={() => toggleImagesCarousel(i)}
                      key={media.Id}
                      alt={media.FileURL}
                      className={classes.PostImage}
                      src={media.MediaType === 'Video' ? media.MediumImageURL : media.FileURL}
                    />
                  );
                })}
              </div>
            )}

            <div className={classes.RightColumn}>
              <div className={classes.PostCommentsContainer}>
                <div className={classes.ContributionsHeader}>
                  {!state.showWhoCanSee && <h2>Contributions</h2>}
                  {props.isPreview || state.showWhoCanSee ? null : (
                    <span
                      style={{ display: state.showContributeForm ? 'none' : 'inline-block' }}
                      onClick={toggleContributeForm}
                      className={classes.OpenContributeForm}
                    >
                      Contribute...
                    </span>
                  )}
                  {!state.showWhoCanSee && (
                    <FontAwesomeIcon icon="eye" className={classes.WhoCanSeeIcon} onClick={() => toggleElement('showWhoCanSee')} />
                  )}
                </div>

                {!props.isPreview && !state.showWhoCanSee && (
                  <div className={classes.ConstributionsSection}>
                    <Element ref={commentsSectionContainer} id="containerElement" className={classes.CommentsAndFormContainer}>
                      <PostDetailComments
                        postTags={post.Tags || []}
                        logId={logIdsForPost.length > 0 ? logIdsForPost[0] : null}
                        postId={params.postId}
                        post={post || null}
                        postingAs={postingAs}
                        closeContributeForm={closeContributeForm}
                        contributeFormOpen={state.showContributeForm}
                        addTagToPost={addTagToPost}
                        addImageToPost={handleAddPostImage}
                        renderCommentImage={renderCommentImage}
                        toggleDeleteCommentDialogue={toggleDeleteCommentDialogue}
                        comments={sortArrayOfObjectsByDateAsc(post.Comments || [], 'Date')}
                        users={post.Users || []}
                      />

                      <div className={PostAddCommenContainerClasses.join(' ')}>
                        <PostDetailCommentForm
                          toggleContributeForm={toggleContributeForm}
                          closeContributeForm={closeContributeForm}
                          logId={logIdsForPost.length > 0 && logIdsForPost[0]}
                          postId={post.Post && post.Post.PostId}
                          commentFormScroll={commentFormScroll}
                          commentType="addComment"
                        />
                        <Element id="comment-form-bottom" />
                      </div>
                    </Element>
                  </div>
                )}
                {state.showWhoCanSee && (
                  <div className={classes.ConstributionsSection}>
                    {' '}
                    <WhoCanSee close={() => toggleElement('showWhoCanSee')} viewers={props.isPreview ? props.whoCanSeeList || [] : whoCanSeeList} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {(mediaFiles !== null || state.renderedCommentImage) && (
        <ImagesCarousel
          show={state.showImagesCarousel}
          images={carouselImages}
          selectedImageIndex={state.selectedImageIndex}
          close={toggleImagesCarousel}
        />
      )}
    </div>
  );
};

export default PostDetail;
